import { FormattedMessage } from 'components/shared/Intl';
import { useSPIntl } from 'hooks/useIntl';
import type { IntlKey } from 'locales';
import { FC } from 'react';
import {
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  type ModalHeaderProps,
  type ModalProps,
  ModalTitle,
} from 'react-bootstrap';

type CloseReason = 'x button' | 'cancel button';
export type ConfirmModalProps = {
  onConfirm: (modalApi: any) => void;
  e2e?: string;
  header?: Partial<ModalHeaderProps>;
  titleClassName?: string;
  bodyClassName?: string;
  confirmBtnClassName?: string;
  cancelBtnClassName?: string;
  confirmBtnKey?: IntlKey;
  confirmBtnIcon?: string;
  titleKey?: IntlKey;
  enableDefaultBody?: boolean;
  withCloseButton?: boolean;
  onClosed: (reason: CloseReason) => void;
} & Omit<Partial<ModalProps>, 'onClosed'>;

export const ConfirmModal: FC<ConfirmModalProps> = ({
  onClosed,
  confirmBtnKey,
  confirmBtnIcon,
  titleKey,
  enableDefaultBody = false,
  titleClassName,
  bodyClassName,
  confirmBtnClassName,
  cancelBtnClassName,
  withCloseButton = false,
  ...props
}) => {
  const { formatMessage } = useSPIntl();
  const handleClose = (reason: CloseReason) => () => onClosed(reason);
  return (
    <Modal
      backdrop={props.backdrop ?? 'static'}
      data-e2e={props.e2e ?? 'modal'}
      className={props.dialogClassName ?? 'confirm-modal-component'}
      {...props}
    >
      <ModalHeader {...props.header}>
        <ModalTitle as="h5" className={titleClassName}>
          <FormattedMessage id={titleKey ?? 'confirmModal.title'} />
        </ModalTitle>
        {withCloseButton && (
          <button
            type="button"
            className="btn btn-xl btn-flat-secondary btn-icon m-12px"
            aria-label={formatMessage({ id: 'close' })}
            onClick={handleClose('x button')}
            data-e2e="modal-close-button"
          >
            <i className="icon">clear</i>
          </button>
        )}
      </ModalHeader>
      <ModalBody className={bodyClassName ?? 'pt-4'} data-e2e="modal-body">
        {props.children}
        {enableDefaultBody && (
          <p>
            <FormattedMessage id="proceedMessage" />
          </p>
        )}
      </ModalBody>
      <ModalFooter>
        <button
          className={cancelBtnClassName ?? 'btn btn-flat-secondary btn-xs'}
          data-e2e="modal-cancel-button"
          onClick={handleClose('cancel button')}
        >
          <FormattedMessage id="cancel" defaultMessage="Cancel" />
        </button>
        <button
          onClick={props.onConfirm}
          className={`${confirmBtnClassName ?? 'btn btn-primary'} ${confirmBtnIcon ? 'btn-icon-end' : ''}`}
          data-e2e="modal-ok-button"
        >
          <FormattedMessage id={confirmBtnKey ?? 'confirmModal.ok'} />
          {confirmBtnIcon && <i className="icon">{confirmBtnIcon}</i>}
        </button>
      </ModalFooter>
    </Modal>
  );
};
